/**
 * Reporting Page
 *
 * Hierarchical visualization of SKI Releases > Products > Request Types
 * Interactive network graph with filtering capabilities
 */

import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import NetworkGraph from '../components/Reporting/NetworkGraph';
import ColumnSelector from '../components/Reporting/ColumnSelector';
import FilterSelector from '../components/Reporting/FilterSelector';
import api from '../services/api';
import { ROUTES } from '../config/constants';
import { exportToCSV } from '../utils/csvExport';
import { TYPE_DISPLAY_NAMES } from '../config/requestTypeConstants';
import './Reporting.css';

const Reporting = () => {
  const history = useHistory();
  const location = useLocation();
  const [hierarchyData, setHierarchyData] = useState([]);
  const [summaryStats, setSummaryStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSkiReleaseId, setSelectedSkiReleaseId] = useState(null);
  const [viewType, setViewType] = useState('by-ski-release'); // or 'by-product' or 'grid-view'
  const [gridData, setGridData] = useState([]);
  const [filters, setFilters] = useState({
    requestType: '',
    status: '',
    owner: '',
    product: '',
    skiRelease: '',
    createdBy: '',
    categorization: '',
  });
  const [filterOptions, setFilterOptions] = useState({
    requestTypes: [],
    statuses: [],
    owners: [],
    products: [],
    skiReleases: [],
    gprSubtypes: [],
    creators: [],
    locations: [],
    categorizations: [],
  });

  const isInitialMount = useRef(true);

  // Column management state
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [showFilterSelector, setShowFilterSelector] = useState(false);
  const [expandAllTrigger, setExpandAllTrigger] = useState(0);
  const manageFiltersRef = useRef(null);

  // Default column configuration
  const defaultColumns = [
    { id: 'product', label: 'Product', required: false, visible: true, defaultVisible: true },
    { id: 'product_type', label: 'Product Type', required: false, visible: true, defaultVisible: true },
    { id: 'ski_release', label: 'SKI Release', required: false, visible: true, defaultVisible: true },
    { id: 'request_type', label: 'Request Type', required: false, visible: true, defaultVisible: true },
    { id: 'status', label: 'Status', required: false, visible: true, defaultVisible: true },
    { id: 'owner', label: 'Owner', required: false, visible: true, defaultVisible: true },
    { id: 'created_by', label: 'Created By', required: false, visible: false, defaultVisible: false },
    { id: 'created_at', label: 'Created Date', required: false, visible: true, defaultVisible: true },
    { id: 'updated_at', label: 'Updated Date', required: false, visible: true, defaultVisible: true },
    { id: 'location', label: 'Location', required: false, visible: false, defaultVisible: false },
    { id: 'work_areas', label: 'Work Areas', required: false, visible: false, defaultVisible: false },
    { id: 'categorization', label: 'Categorization', required: false, visible: false, defaultVisible: false },
  ];

  // Load saved column preferences from localStorage
  const [gridColumns, setGridColumns] = useState(() => {
    const saved = localStorage.getItem('reporting_grid_columns');
    if (saved) {
      try {
        const savedColumns = JSON.parse(saved);
        // Merge with defaults to handle new columns
        return defaultColumns.map(col => {
          const savedCol = savedColumns.find(s => s.id === col.id);
          return savedCol ? { ...col, visible: savedCol.visible } : col;
        });
      } catch (e) {
        console.error('Error loading column preferences:', e);
        return defaultColumns;
      }
    }
    return defaultColumns;
  });

  // Save column preferences to localStorage
  const handleColumnsChange = (newColumns) => {
    setGridColumns(newColumns);
    localStorage.setItem('reporting_grid_columns', JSON.stringify(newColumns));
  };

  // Default filter configuration
  const defaultFilterConfig = [
    { id: 'requestType', label: 'Request Type', type: 'select', required: false, visible: true, defaultVisible: true },
    { id: 'status', label: 'Status', type: 'select', required: false, visible: true, defaultVisible: true },
    { id: 'owner', label: 'Owner', type: 'select', required: false, visible: true, defaultVisible: true },
    { id: 'product', label: 'Product', type: 'select', required: false, visible: true, defaultVisible: true },
    { id: 'skiRelease', label: 'SKI Release', type: 'select', required: false, visible: true, defaultVisible: true },
    { id: 'createdBy', label: 'Created By', type: 'select', required: false, visible: false, defaultVisible: false },
    { id: 'categorization', label: 'Categorization', type: 'select', required: false, visible: false, defaultVisible: false },
  ];

  // Load saved filter configuration from localStorage
  const [filterConfig, setFilterConfig] = useState(() => {
    const saved = localStorage.getItem('reporting_filter_config');
    if (saved) {
      try {
        const savedConfig = JSON.parse(saved);
        // Merge with defaults to handle new filters
        return defaultFilterConfig.map(filter => {
          const savedFilter = savedConfig.find(s => s.id === filter.id);
          return savedFilter ? { ...filter, visible: savedFilter.visible } : filter;
        });
      } catch (e) {
        console.error('Error loading filter configuration:', e);
        return defaultFilterConfig;
      }
    }
    return defaultFilterConfig;
  });

  // Save filter configuration to localStorage
  const handleFilterConfigChange = (newConfig) => {
    setFilterConfig(newConfig);
    localStorage.setItem('reporting_filter_config', JSON.stringify(newConfig));
  };

  /**
   * Fetch filter options
   */
  const fetchFilterOptions = async () => {
    try {
      const [filterResponse, productsResponse, skiReleasesResponse] = await Promise.all([
        api.getFilterOptions(),
        api.getProducts({ status: 'active' }),
        api.getSkiReleases(),
      ]);

      const filterData = filterResponse.data.data;
      const products = productsResponse.data.data.products.map(p => p.product_name);
      const skiReleases = skiReleasesResponse.data.data.skiReleases; // full objects {id, full_name}

      setFilterOptions({
        requestTypes: filterData.requestTypes || [],
        statuses: filterData.statuses || [],
        owners: filterData.owners || [],
        gprSubtypes: filterData.gprSubtypes || [],
        creators: filterData.creators || [],
        locations: filterData.locations || [],
        categorizations: filterData.categorizations || [],
        products,
        skiReleases,
      });
    } catch (err) {
      console.error('Error fetching filter options:', err);
    }
  };

  /**
   * Fetch grid data for Grid View
   */
  const fetchGridData = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: 1,
        limit: 1000,
        _t: Date.now(), // Cache busting to ensure fresh data
      };

      // Apply filters
      if (filters.requestType) params.request_type = filters.requestType;
      if (filters.status) params.status = filters.status;
      if (filters.owner) params.owner = filters.owner;
      if (filters.product) params.product_name = filters.product;
      if (filters.skiRelease) params.ski_release = filters.skiRelease;
      if (filters.createdBy) params.created_by = filters.createdBy;
      if (filters.categorization) params.categorization = filters.categorization;

      const response = await api.getRequests(params);
      const requests = response.data.data.requests || [];

      // Sort by Product (asc), Ski Release (desc), Request Type (asc)
      const sorted = [...requests].sort((a, b) => {
        const productA = (a.product?.product_name || '').toLowerCase();
        const productB = (b.product?.product_name || '').toLowerCase();
        if (productA !== productB) return productA.localeCompare(productB);

        const skiA = a.skiRelease?.full_name || '';
        const skiB = b.skiRelease?.full_name || '';
        if (skiA !== skiB) return skiB.localeCompare(skiA); // descending

        const rtA = (TYPE_DISPLAY_NAMES[a.requestType?.type_code] || a.requestType?.type_code || '').toLowerCase();
        const rtB = (TYPE_DISPLAY_NAMES[b.requestType?.type_code] || b.requestType?.type_code || '').toLowerCase();
        return rtA.localeCompare(rtB);
      });

      setGridData(sorted);

      // Update summary stats
      setSummaryStats({
        totalRequests: requests.length,
        totalProducts: [...new Set(requests.map(r => r.product?.product_name).filter(Boolean))].length,
        totalSkiReleases: [...new Set(requests.map(r => r.skiRelease?.full_name).filter(Boolean))].length,
      });
    } catch (err) {
      console.error('Error fetching grid data:', err);
      setError(err.message || 'Failed to load grid data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch hierarchy data
   */
  const fetchHierarchyData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build params with filters, converting skiRelease name → skiReleaseId for the API
      const params = {};

      Object.keys(filters).forEach((key) => {
        if (filters[key]) {
          if (key === 'skiRelease') {
            const found = filterOptions.skiReleases.find(sr => sr.full_name === filters[key]);
            if (found) params.skiReleaseId = found.id;
          } else {
            params[key] = filters[key];
          }
        }
      });

      // Add viewType parameter
      params.viewType = viewType;

      const [hierarchyResponse, summaryResponse] = await Promise.all([
        api.getHierarchyReport(params),
        api.getSummaryStats(params),
      ]);

      setHierarchyData(hierarchyResponse.data.data.hierarchy);
      setSummaryStats(summaryResponse.data.data.summary);
    } catch (err) {
      console.error('Error fetching report data:', err);
      setError(err.message || 'Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Initial data fetch
   */
  useEffect(() => {
    fetchHierarchyData();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.key ?? 0]);

  /**
   * Refetch data when selectedSkiReleaseId or viewType changes
   */
  useEffect(() => {
    if (selectedSkiReleaseId !== null) {
      fetchHierarchyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSkiReleaseId]);

  /**
   * Refetch data when viewType changes
   */
  useEffect(() => {
    if (viewType === 'grid-view') {
      fetchGridData();
    } else {
      fetchHierarchyData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType]);

  /**
   * Auto-fetch when filters change, debounced to avoid rapid API calls
   */
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      if (viewType === 'grid-view') {
        fetchGridData();
      } else {
        fetchHierarchyData();
      }
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  /**
   * Handle filter change
   */
  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  /**
   * Clear filters — debounced effect handles the re-fetch
   */
  const handleClearFilters = () => {
    setFilters({
      requestType: '',
      status: '',
      owner: '',
      product: '',
      skiRelease: '',
      createdBy: '',
      categorization: '',
    });
    setSelectedSkiReleaseId(null);
  };

  /**
   * Handle node click - expand/collapse nodes (no filtering)
   */
  const handleNodeClick = (node) => {
    // Node expansion/collapse is handled by NetworkGraph component
    // No filtering behavior - all SKI releases remain visible
    // Users can use the filter controls if they want to filter
  };

  /**
   * Expand all nodes
   */
  const handleExpandAll = () => {
    setExpandAllTrigger(prev => prev + 1);
  };

  /**
   * Handle CSV download of grid data
   */
  const handleDownloadCSV = () => {
    const timestamp = new Date().toISOString().split('T')[0];
    const filename = `requests-report-${timestamp}.csv`;
    exportToCSV(gridData, gridColumns, filename);
  };

  /**
   * Handle Products CSV download for the selected SKI release
   */
  const handleDownloadProductsCSV = async () => {
    try {
      const skiReleaseName = filters.skiRelease || '';
      const response = await api.exportProducts(skiReleaseName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Products_${skiReleaseName || 'All'}_${new Date().toISOString().slice(0, 10).replace(/-/g, '')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading products CSV:', err);
      alert('Failed to download products CSV: ' + (err.message || 'Unknown error'));
    }
  };

  /**
   * Render a single filter input based on filter configuration
   */
  const renderFilterInput = (filter) => {
    if (!filter.visible) return null;

    if (filter.type === 'text') {
      return (
        <div key={filter.id} className="reporting__filter-group">
          <label htmlFor={filter.id} className="reporting__filter-label">
            {filter.label}
          </label>
          <input
            type="text"
            id={filter.id}
            value={filters[filter.id]}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            className="reporting__filter-input"
            placeholder={`Search ${filter.label.toLowerCase()}...`}
          />
        </div>
      );
    }

    if (filter.type === 'select') {
      let options = [];
      let allLabel = `All ${filter.label}`;

      switch (filter.id) {
        case 'requestType':
          options = filterOptions.requestTypes;
          break;
        case 'status':
          options = filterOptions.statuses;
          allLabel = 'All Statuses';
          break;
        case 'owner':
          options = filterOptions.owners;
          break;
        case 'product':
          options = filterOptions.products;
          break;
        case 'skiRelease':
          options = filterOptions.skiReleases.map(sr => sr.full_name);
          break;
        case 'createdBy':
          options = filterOptions.creators.map(c => c.name);
          break;
        case 'categorization':
          options = filterOptions.categorizations;
          break;
        default:
          options = [];
      }

      return (
        <div key={filter.id} className="reporting__filter-group">
          <label htmlFor={filter.id} className="reporting__filter-label">
            {filter.label}
          </label>
          <select
            id={filter.id}
            value={filters[filter.id]}
            onChange={(e) => handleFilterChange(filter.id, e.target.value)}
            className="reporting__filter-input"
          >
            <option value="">{allLabel}</option>
            {options
              .filter(option => option !== 'GPR')
              .map((option) => (
                <option key={option} value={option}>
                  {TYPE_DISPLAY_NAMES[option] || option}
                </option>
              ))}
          </select>
        </div>
      );
    }

    return null;
  };

  if (loading && hierarchyData.length === 0) {
    return (
      <div className="reporting">
        <div className="reporting__loading">
          <div className="reporting__spinner" />
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="reporting">
        <div className="reporting__error">
          <svg width="48" height="48" viewBox="0 0 20 20" fill="currentColor">
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
          <h2>Error Loading Report</h2>
          <p>{error}</p>
          <button onClick={fetchHierarchyData} className="reporting__retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="reporting">
      {/* Header */}
      <div className="reporting__header">
        <div>
          <h1 className="reporting__title">Product Launch Reporting</h1>
          <p className="reporting__subtitle">
            Hierarchical view of SKI Releases, Products, and Request Types
          </p>
        </div>
        <button
          className="reporting__back-btn"
          onClick={() => history.push(ROUTES.DASHBOARD)}
        >
          Back to Dashboard
        </button>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <div className="reporting__stats">
          <div className="reporting__stat-card">
            <div className="reporting__stat-value">{summaryStats.totalSkiReleases}</div>
            <div className="reporting__stat-label">SKI Releases</div>
          </div>
          <div className="reporting__stat-card">
            <div className="reporting__stat-value">{summaryStats.totalProducts}</div>
            <div className="reporting__stat-label">Products</div>
          </div>
          <div className="reporting__stat-card">
            <div className="reporting__stat-value">{summaryStats.totalRequests}</div>
            <div className="reporting__stat-label">Total Requests</div>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="reporting__view-toggle">
        <button
          className={`reporting__view-btn ${viewType === 'by-ski-release' ? 'reporting__view-btn--active' : ''}`}
          onClick={() => setViewType('by-ski-release')}
        >
          By SKI Release
        </button>
        <button
          className={`reporting__view-btn ${viewType === 'by-product' ? 'reporting__view-btn--active' : ''}`}
          onClick={() => setViewType('by-product')}
        >
          By Product
        </button>
        <button
          className={`reporting__view-btn ${viewType === 'by-release-type' ? 'reporting__view-btn--active' : ''}`}
          onClick={() => setViewType('by-release-type')}
        >
          By Release Type
        </button>
        <button
          className={`reporting__view-btn ${viewType === 'by-hierarchy' ? 'reporting__view-btn--active' : ''}`}
          onClick={() => setViewType('by-hierarchy')}
        >
          By Hierarchy
        </button>
        <button
          className={`reporting__view-btn ${viewType === 'grid-view' ? 'reporting__view-btn--active' : ''}`}
          onClick={() => setViewType('grid-view')}
        >
          Grid View
        </button>
      </div>

      {/* Filters */}
      <div className="reporting__filters">
        {/* Top row: filter dropdowns */}
        <div className="reporting__filters-top">
          {filterConfig.map(filter => renderFilterInput(filter))}
        </div>

        {/* Bottom row: download left, action buttons right */}
        <div className="reporting__filter-actions">
          {viewType !== 'grid-view' && (
            <button className="reporting__download-btn" onClick={handleDownloadProductsCSV}>
              <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download CSV
            </button>
          )}
          <div className="reporting__filter-actions-right">
            <button onClick={handleClearFilters} className="reporting__filter-btn reporting__filter-btn--secondary">
              Clear Filters
            </button>
            {viewType !== 'grid-view' && (
              <button onClick={handleExpandAll} className="reporting__filter-btn reporting__filter-btn--secondary">
                Expand All
              </button>
            )}
            <div style={{ position: 'relative' }}>
              <button
                ref={manageFiltersRef}
                className="reporting__filter-btn reporting__filter-btn--secondary"
                onClick={() => setShowFilterSelector(!showFilterSelector)}
              >
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor" style={{ marginRight: '5px' }}>
                  <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                </svg>
                Manage Filters
              </button>
              {showFilterSelector && (
                <FilterSelector
                  filters={filterConfig}
                  onFiltersChange={handleFilterConfigChange}
                  onClose={() => setShowFilterSelector(false)}
                  triggerRef={manageFiltersRef}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Network Graph or Grid View */}
      {viewType === 'grid-view' ? (
        <div className="reporting__grid-container">
          <div className="reporting__grid-header">
            <button
              className="reporting__download-csv-btn"
              onClick={handleDownloadCSV}
              disabled={gridData.length === 0}
            >
              <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
              Download CSV
            </button>
            <div className="reporting__column-selector-wrapper">
              <button
                className="reporting__manage-columns-btn"
                onClick={() => setShowColumnSelector(!showColumnSelector)}
              >
                <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 16a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" />
                </svg>
                Manage Columns
              </button>
              {showColumnSelector && (
                <ColumnSelector
                  columns={gridColumns}
                  onColumnsChange={handleColumnsChange}
                  onClose={() => setShowColumnSelector(false)}
                />
              )}
            </div>
          </div>
          <div className="reporting__grid-wrapper">
            <table className="reporting__grid-table">
              <thead>
                <tr>
                  {gridColumns.filter(col => col.visible).map(column => (
                    <th key={column.id}>{column.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridData.length === 0 ? (
                  <tr>
                    <td colSpan={gridColumns.filter(col => col.visible).length} className="reporting__grid-empty">
                      No requests found matching the current filters.
                    </td>
                  </tr>
                ) : (
                  gridData.map((request) => (
                    <tr
                      key={request.id}
                      className="reporting__grid-row"
                      onClick={() => history.push(`/request/${request.id}`, { from: 'reporting' })}
                      style={{ cursor: 'pointer' }}
                    >
                      {gridColumns.filter(col => col.visible).map(column => {
                        // Render cell content based on column id
                        let content;
                        switch (column.id) {
                          case 'request_number':
                            content = request.request_number;
                            break;
                          case 'product':
                            content = request.product?.product_name || '-';
                            break;
                          case 'product_type':
                            content = request.product?.product_type
                              ? request.product.product_type.charAt(0) + request.product.product_type.slice(1).toLowerCase()
                              : '-';
                            break;
                          case 'ski_release':
                            content = request.skiRelease?.full_name || '-';
                            break;
                          case 'request_type':
                            const reqTypeCode = request.requestType?.type_code || '-';
                            if (request.gpr_subtype) {
                              content = `GPR - ${request.gpr_subtype}`;
                            } else if (reqTypeCode === 'GPR' && request.answers) {
                              const gprSubtypeAnswer = request.answers.find(a => a.question_key === 'gpr_subtype');
                              content = gprSubtypeAnswer?.answer_value
                                ? `GPR - ${gprSubtypeAnswer.answer_value}`
                                : (TYPE_DISPLAY_NAMES[reqTypeCode] || reqTypeCode);
                            } else {
                              content = TYPE_DISPLAY_NAMES[reqTypeCode] || reqTypeCode;
                            }
                            break;
                          case 'status':
                            content = (
                              <span className={`reporting__grid-status reporting__grid-status--${request.status}`}>
                                {request.status}
                              </span>
                            );
                            break;
                          case 'owner':
                            content = request.owner ? `${request.owner.first_name} ${request.owner.last_name}` : '-';
                            break;
                          case 'created_by':
                            content = request.creator ? `${request.creator.first_name} ${request.creator.last_name}` : '-';
                            break;
                          case 'created_at':
                            content = new Date(request.created_at).toLocaleDateString();
                            break;
                          case 'updated_at':
                            content = new Date(request.updated_at).toLocaleDateString();
                            break;
                          case 'location':
                            content = request.location || '-';
                            break;
                          case 'work_areas':
                            content = request.work_areas || '-';
                            break;
                          case 'categorization':
                            content = request.product?.categorization || '-';
                            break;
                          default:
                            content = '-';
                        }
                        return <td key={column.id}>{content}</td>;
                      })}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="reporting__graph">
          <NetworkGraph hierarchyData={hierarchyData} onNodeClick={handleNodeClick} expandAllTrigger={expandAllTrigger} />
        </div>
      )}

      {/* Legend */}
      <div className="reporting__legend">
        <h3 className="reporting__legend-title">Legend</h3>
        <div className="reporting__legend-items">
          {viewType === 'by-ski-release' ? (
            <>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--ski"></div>
                <span>SKI Release (Click to expand request types)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--request"></div>
                <span>Request Type (Click to expand products)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--product"></div>
                <span>Product (Click to open request)</span>
              </div>
            </>
          ) : viewType === 'by-release-type' ? (
            <>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--ski"></div>
                <span>SKI Release (click to expand release types)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#5eadd4' }}></div>
                <span>Release Type — EA / RA / GA (click to expand request types)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--request"></div>
                <span>Request Type (click to expand products)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--product"></div>
                <span>Product (shows status badge and product-type icon)</span>
              </div>
            </>
          ) : viewType === 'by-hierarchy' ? (
            <>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#5b21b6' }}></div>
                <span>Packages / Unpackaged (click to expand)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#7c3aed' }}></div>
                <span>Package (click to expand commercial products; count = # commercial products)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#0d9488' }}></div>
                <span>Commercial Product (click to expand; count = # technical products)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#0891b2' }}></div>
                <span>SKI Releases (click to expand; count = # distinct SKI releases)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--ski"></div>
                <span>SKI Release (click to expand request types; count = # distinct request types)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color" style={{ background: '#b45309' }}></div>
                <span>Technical Product (shown for structure; count shown when linked)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--request"></div>
                <span>Request Type (click to open request; count = # those requests)</span>
              </div>
            </>
          ) : (
            <>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--product"></div>
                <span>Product (Click to expand SKI releases)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--ski"></div>
                <span>SKI Release (Click to expand request types)</span>
              </div>
              <div className="reporting__legend-item">
                <div className="reporting__legend-color reporting__legend-color--request"></div>
                <span>Request Type (Shows status in brackets)</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reporting;
